import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import PageHeader from "./-components/PageHeader";
import ConsoleHrozontalHeadTable, { DataListProps } from "./-components/ConsolehorizontalHeadTable";
import LoadingPop from "components/popup/LoadingPop";
import { toastErrorSyle } from "hooks/utils/util";
import { usePutConsoleQrSystem } from "hooks/console/usePutConsoleQrSystem";
import { DetailResponse, useGetConsoleQrSystemDetail } from "hooks/console/useGetConsoleQrSystemDetail";
import Comment from "./-components/Comment";
import { usePostConsoleQrRequestComment } from "hooks/console/usePostConsoleQrRequestComment";
import { usePutConsoleQrRequestComment } from "hooks/console/usePutConsoleQrRequestComment";
import { useDeleteConsoleQrRequestComment } from "hooks/console/useDeleteConsoleQrRequestComment";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { useDeleteConsoleQrSystem } from "hooks/console/useDeleteConsoleQrSystem";
import Seo from "components/Seo";

const initialForm = {
    list_no: 0,
    company: "",
    name: "",
    mobile: "",
    address: "",
    printer: "",
    w_date: "",
    result: 0,
    result_text: "",
    comment: [],
};

export default function ConsoleQrRequestDetail() {
    const navigate = useNavigate();
    const params = useParams();
    const idx = params.idx;
    const [form, setForm] = useState<DetailResponse>(initialForm);
    const { data, isLoading, refetch } = useGetConsoleQrSystemDetail({ list_no: idx ?? "" });
    const { mutate: update } = usePutConsoleQrSystem();
    const { mutate: deleteRequest } = useDeleteConsoleQrSystem();

    const { mutate: postComment } = usePostConsoleQrRequestComment();
    const { mutate: updateComment } = usePutConsoleQrRequestComment();
    const { mutate: deleteComment } = useDeleteConsoleQrRequestComment();

    useEffect(() => {
        if (data) {
            setForm({
                list_no: data.list_no,
                company: data.company,
                name: data.name,
                mobile: data.mobile,
                address: data.address,
                printer: data.printer,
                w_date: data.w_date,
                result: data.result,
                result_text: data.result_text,
                comment: data.comment,
            });
        }
    }, [data]);

    function handleChangeRowStatus(row: DataListProps, value: string) {
        update(
            {
                list_no: row.list_no,
                result: value,
            },
            {
                onSuccess: () => {
                    toast.success("성공적으로 수정 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    }

    const handleDeleteRequest = () => {
        if (data) {
            deleteRequest(
                { list_no: [data.list_no] },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 삭제 되었습니다.");
                        navigate(-1);
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };

    const thead = [
        {
            title: "처리상태",
            key: "result",
            content(row: any, tbodyData: DataListProps) {
                return (
                    <div className="flex">
                        <Select defaultValue={tbodyData.result.toString()} onValueChange={value => handleChangeRowStatus(tbodyData, value)}>
                            <SelectTrigger
                                className={`relative text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2  border-none shadow-none max-w-[120px] justify-center ${row.result === 7 ? "text-[#4F46E5]" : "text-[#999999]"}`}
                            >
                                <SelectValue placeholder="" />
                                <ArrowDown className="[&_path]:fill-[#1C1B1F]" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="1">접수대기</SelectItem>
                                <SelectItem value="2">중복건</SelectItem>
                                <SelectItem value="3">진행보류</SelectItem>
                                <SelectItem value="4">설치거부</SelectItem>
                                <SelectItem value="5">프린터발송</SelectItem>
                                <SelectItem value="6">A4라벨지</SelectItem>
                                <SelectItem value="7">설치완료</SelectItem>
                                <SelectItem value="8">기타</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                );
            },
        },
        { title: "신청일", key: "w_date", tdClassName: "px-5" },
        {
            title: "상호명",
            key: "company",
            thClassName: "whitespace-nowrap",
            tdClassName: "px-5",
            content: (value: string) => <span>{value}</span>,
        },
        {
            title: "주소",
            key: "address",
            thClassName: "whitespace-nowrap",
            tdClassName: "px-5",
            content: (value: string) => <span>{value}</span>,
        },
        {
            title: "담당자명",
            key: "name",
            thClassName: "whitespace-nowrap",
            tdClassName: "px-5",
            content: (value: string) => <span>{value}</span>,
        },
        {
            title: "연락처",
            key: "mobile",
            thClassName: "whitespace-nowrap",
            tdClassName: "px-5",
            content: (value: string) => <span>{value}</span>,
        },
        {
            title: "프린터",
            key: "printer",
            thClassName: "whitespace-nowrap",
            tdClassName: "px-5",
            content: (value: string) => <span>{value}</span>,
        },
    ];

    // 코멘트 등록
    const handlePostComment = (value: string, successCallback: () => void) => {
        if (idx) {
            postComment(
                {
                    list_no: idx,
                    c_content: value,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 등록 되었습니다.");
                        refetch();
                        successCallback();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };
    // 코멘트 수정
    const handleUpdateComment = (cListNo: string, content: string, callback: () => void) => {
        if (idx) {
            updateComment(
                {
                    list_no: idx,
                    c_list_no: cListNo,
                    c_content: content,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 수정 되었습니다.");
                        refetch();
                        callback();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };
    // 코멘트 삭제
    const handleDeleteComment = (cListNo: string) => {
        if (idx) {
            deleteComment(
                {
                    list_no: idx,
                    c_list_no: cListNo,
                },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 삭제 되었습니다.");
                        refetch();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
    };

    return (
        <>
            <Seo type="console" pageTitle="QR시스템 사용 코드 변경 신청" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="QR시스템 사용 코드 변경 신청" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className=" px-[40px] py-[28px] w-full overflow-auto">
                        {isLoading ? (
                            <LoadingPop />
                        ) : (
                            <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[800px]">
                                {data ? (
                                    <>
                                        <ConsoleHrozontalHeadTable thead={thead} tbody={data} />
                                        <div className="flex justify-center gap-x-[8px] mt-5">
                                            <button
                                                onClick={handleDeleteRequest}
                                                className="text-[#666666] text-[18px] bg-white rounded-[5px] border border-[#dddddd] px-[55px] py-[15px]"
                                            >
                                                삭제
                                            </button>
                                            <button
                                                onClick={() => navigate(-1)}
                                                className="text-white text-[18px] bg-[#4F46E5] rounded-[5px] border border-[#666666] px-[55px] py-[15px]"
                                            >
                                                목록
                                            </button>
                                        </div>

                                        <Comment
                                            adminOnly
                                            commentData={form.comment}
                                            postComment={handlePostComment}
                                            updateComment={handleUpdateComment}
                                            deleteComment={handleDeleteComment}
                                        />
                                    </>
                                ) : (
                                    <div className="flex flex-col gap-5 items-center justify-center py-[100px]">
                                        <p>결과가 없습니다.</p>
                                        <button onClick={() => navigate(-1)} className="rounded-[8px] border px-5 py-2">
                                            뒤로가기
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
